import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api, apiFile, apiRaw, enc, saveBlob, type Bin, type BinDetail } from '../api';
import { useAuth } from '../auth';
import { ScanField } from '../components/ScanField';
import { StepHeading } from '../components/Help';
import { Empty, ErrorBox, Field, Loading, LotKindBadge, Success, Thumb, Val, fmtDate } from '../components/ui';
import { useLocations } from '../hooks';

/** Ask for a reprint reason when the server says the label was printed before, then download. */
async function printBinLabels(bins: { id: string }[], filename: string) {
  const binIds = bins.map((b) => b.id);
  try {
    saveBlob(await apiFile('/bins/labels', { binIds }), filename);
  } catch (e) {
    if (!(e instanceof ApiError && e.code === 'reprint_reason_required')) throw e;
    const reason = prompt(`${e.message}\n\nReason for printing again:`)?.trim();
    if (!reason) return false;
    saveBlob(await apiFile('/bins/labels', { binIds, reason }), filename);
  }
  return true;
}

export function BinsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const nav = useNavigate();
  const qc = useQueryClient();
  const locations = useLocations();
  const [loc, setLoc] = useState('');
  const [showRetired, setShowRetired] = useState(false);
  const params = new URLSearchParams();
  if (loc) params.set('locationId', loc);
  if (showRetired) params.set('all', '1');
  const bins = useQuery({ queryKey: ['bins', params.toString()], queryFn: () => api<{ bins: Bin[] }>(`/bins?${params}`).then((r) => r.bins) });
  const [error, setError] = useState<unknown>(null);

  const open = async (code: string) => {
    setError(null);
    const { status, body } = await apiRaw(`/bins/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
    if (status === 200) nav(`/bins/${body.bin.id}`);
    else setError(new Error(body?.error?.message ?? 'Could not reach the server'));
  };

  return (
    <div>
      <h1>Bins</h1>
      <p className="muted">
        Bins carry lots from the warehouse to the store. Pack lots into a bin, then move the bin: every lot in it moves with it. Each bin has its own label (BIN00001…).
      </p>
      <div className="grid2">
        <section className="card">
          <StepHeading tip="bins">Open a bin</StepHeading>
          <ScanField kind="bin" onScan={open} autoFocus />
          <ErrorBox error={error} />
        </section>
        {isAdmin && <CreateBins onCreated={() => qc.invalidateQueries({ queryKey: ['bins'] })} />}
      </div>

      <section className="card">
        <div className="filters">
          <select value={loc} onChange={(e) => setLoc(e.target.value)} aria-label="Location">
            <option value="">All locations</option>
            {locations.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.code}
              </option>
            ))}
          </select>
          <label className="check">
            <input type="checkbox" checked={showRetired} onChange={(e) => setShowRetired(e.target.checked)} /> Show retired bins
          </label>
        </div>
        {bins.isLoading && <Loading />}
        <ErrorBox error={bins.error} onRetry={() => bins.refetch()} />
        {bins.data?.length === 0 && <Empty>No bins yet.{isAdmin ? ' Create some above.' : ' An administrator can create them.'}</Empty>}
        {!!bins.data?.length && (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Bin</th>
                  <th>Location</th>
                  <th>Lots</th>
                  <th>Label</th>
                  <th>Notes</th>
                  <th>Created</th>
                </tr>
              </thead>
              <tbody>
                {bins.data.map((b) => (
                  <tr key={b.id} className="clickable" onClick={() => nav(`/bins/${b.id}`)}>
                    <td className="mono">
                      <Link to={`/bins/${b.id}`}>{b.code}</Link> {!b.active && <span className="badge">retired</span>}
                    </td>
                    <td>
                      <Val v={b.locationCode} missing="none" />
                    </td>
                    <td>{b.lotCount}</td>
                    <td>{b.printCount === 0 ? <span className="badge">not printed</span> : 'printed'}</td>
                    <td>{b.notes}</td>
                    <td className="small">{fmtDate(b.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function CreateBins({ onCreated }: { onCreated: () => void }) {
  const locations = useLocations();
  const [qty, setQty] = useState('10');
  const [locationId, setLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = Number(qty);
    if (!Number.isInteger(n) || n < 1 || n > 200) return setError(new Error('Enter a quantity from 1 to 200'));
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const r = await api<{ bins: { id: string; code: string }[] }>('/bins', { body: { quantity: n, locationId: locationId || null } });
      const first = r.bins[0].code;
      const last = r.bins[r.bins.length - 1].code;
      onCreated();
      await printBinLabels(r.bins, `bin-labels-${first}-${last}.pdf`);
      setMsg(`Created ${n} bin(s): ${first} to ${last}. The label PDF was downloaded: print it at actual size on 4″ × 6″ labels.`);
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form className="card" onSubmit={submit}>
      <h2>New bins + labels</h2>
      <div className="grid2">
        <Field label="How many">
          <input value={qty} onChange={(e) => setQty(e.target.value)} inputMode="numeric" />
        </Field>
        <Field label="Starting location (optional)">
          <select value={locationId} onChange={(e) => setLocationId(e.target.value)}>
            <option value="">— none —</option>
            {locations.data?.map((l) => (
              <option key={l.id} value={l.id}>
                {l.code}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <ErrorBox error={error} />
      {msg && <Success>{msg}</Success>}
      <button className="btn btn-primary" disabled={busy}>
        {busy ? 'Creating…' : 'Create bins and print labels'}
      </button>
    </form>
  );
}

// ------------------------------------------------------------------ detail

export function BinDetailPage() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['bin', id], queryFn: () => api<{ bin: BinDetail }>(`/bins/${id}`).then((r) => r.bin) });
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['bin', id] });
    qc.invalidateQueries({ queryKey: ['bins'] });
  };
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);

  if (q.isLoading) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} onRetry={() => q.refetch()} />;
  const bin = q.data!;

  const run = async (fn: () => Promise<string | null | void>) => {
    if (busy) return;
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const m = await fn();
      if (m) setMsg(m);
      refresh();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const takeOut = (lotId: string, lotNumber: string) =>
    run(async () => {
      await api(`/bins/${bin.id}/unpack`, { body: { lotId } });
      return `${lotNumber} taken out of ${bin.code}. It stays at ${bin.locationCode ?? 'no location'} until it is moved.`;
    });

  return (
    <div className="narrow">
      <div className="lot-head">
        <div>
          <div className="muted small">Bin</div>
          <div className="lot-number mono">{bin.code}</div>
          {!bin.active && <span className="badge">retired</span>}
        </div>
        <div className="lot-loc">
          <div className="muted small">Current location</div>
          <div className="loc-big">{bin.locationCode ?? <span className="missing">none</span>}</div>
          <div className="muted small">
            {bin.lotCount} lot{bin.lotCount === 1 ? '' : 's'} inside
          </div>
        </div>
      </div>
      {msg && <Success>{msg}</Success>}
      <ErrorBox error={error} />

      {bin.active && <PackLots bin={bin} onChange={refresh} />}
      {bin.active && <MoveBin bin={bin} onMoved={refresh} />}

      <section className="card">
        <div className="row between">
          <h2>Contents ({bin.lots.length})</h2>
          <a className="btn btn-small" href={`/api/bins/${bin.id}/packing-list.pdf`} target="_blank" rel="noreferrer">
            Packing list (PDF)
          </a>
        </div>
        {bin.lots.length === 0 && <p className="muted">This bin is empty.</p>}
        {bin.lots.length > 0 && (
          <ul className="pick-list">
            {bin.lots.map((l) => (
              <li key={l.id} className="row gap">
                <Thumb id={l.primaryPhotoId} size={44} />
                <div style={{ flex: 1 }}>
                  <Link to={`/lots/${l.id}`} className="mono">
                    {l.lotNumber}
                  </Link>{' '}
                  <span className="small">
                    <Val v={l.title} missing="no title" />
                  </span>
                  <LotKindBadge lot={l} />
                  <div className="muted small">
                    {l.productItemNumber ? `item ${l.productItemNumber}` : 'mixed lot'}
                    {l.conditionLabel ? ` · ${l.conditionLabel}` : ''}
                    {l.colour ? ` · ${l.colour}` : ''}
                  </div>
                </div>
                <button className="btn btn-small btn-ghost" disabled={busy} onClick={() => takeOut(l.id, l.lotNumber)}>
                  Take out
                </button>
              </li>
            ))}
          </ul>
        )}
        {bin.lots.length > 0 && (
          <button
            className="btn btn-small"
            disabled={busy}
            onClick={() =>
              confirm(`Take all ${bin.lots.length} lots out of ${bin.code}? They stay at ${bin.locationCode ?? 'no location'}.`) &&
              run(async () => {
                const r = await api<{ removed: number }>(`/bins/${bin.id}/unpack`, { body: { emptyAll: true } });
                return `${r.removed} lot(s) taken out. ${bin.code} is empty and can be reused.`;
              })
            }
          >
            Empty the bin
          </button>
        )}
      </section>

      {bin.locationHistory.length > 0 && (
        <section className="card">
          <details>
            <summary>Bin location history ({bin.locationHistory.length})</summary>
            <ul className="history">
              {bin.locationHistory.map((h, i) => (
                <li key={i}>
                  {fmtDate(h.movedAt)}: {h.fromCode ?? '—'} → <strong>{h.toCode ?? '— none'}</strong> with {h.lotCount} lot(s) by {h.movedByName}
                  {h.note ? ` (${h.note})` : ''}
                </li>
              ))}
            </ul>
          </details>
        </section>
      )}

      {isAdmin && (
        <section className="card">
          <h2>Administrator</h2>
          <div className="row gap">
            <button className="btn" disabled={busy} onClick={() => run(async () => ((await printBinLabels([bin], `bin-label-${bin.code}.pdf`)) ? 'Label downloaded.' : null))}>
              {bin.printCount === 0 ? 'Print label' : 'Reprint label…'}
            </button>
            <button
              className="btn btn-ghost"
              disabled={busy}
              onClick={() =>
                run(async () => {
                  await api(`/bins/${bin.id}`, { method: 'PATCH', body: { version: bin.version, active: !bin.active } });
                  return bin.active ? `${bin.code} retired.` : `${bin.code} is back in use.`;
                })
              }
            >
              {bin.active ? 'Retire bin' : 'Reactivate bin'}
            </button>
          </div>
          <p className="muted small">Created {fmtDate(bin.createdAt)} by {bin.createdByName}. A bin can only be retired when it is empty.</p>
        </section>
      )}
    </div>
  );
}

interface PackResult {
  at: string;
  code: string;
  ok: boolean;
  text: string;
}

function PackLots({ bin, onChange }: { bin: BinDetail; onChange: () => void }) {
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<PackResult[]>([]);
  const pack = async (code: string) => {
    setBusy(true);
    const at = new Date().toISOString();
    try {
      const r = await api<{ unchanged: boolean; lotNumber: string; fromBin: string | null; lotCount: number }>(`/bins/${bin.id}/pack`, { body: { lotNumber: code } });
      const text = r.unchanged ? 'already in this bin' : r.fromBin ? `moved here from ${r.fromBin}` : 'packed';
      setResults((x) => [{ at, code: r.lotNumber, ok: true, text: `${text} (${r.lotCount} in bin)` }, ...x]);
      onChange();
    } catch (e) {
      setResults((x) => [{ at, code, ok: false, text: (e as Error).message }, ...x]);
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="card">
      <h2>Pack lots into {bin.code}</h2>
      <ScanField kind="lot" label="SCAN LOTS INTO THIS BIN" onScan={pack} busy={busy} autoFocus />
      <p className="muted small">Each lot takes the bin's location. A lot that was in another bin is moved out of it.</p>
      {results.length > 0 && (
        <ul className="results">
          {results.map((r, i) => (
            <li key={i} className={r.ok ? 'ok' : 'bad'}>
              <span className="mono">{r.code}</span> {r.ok ? '✓' : '✗'} {r.text} <span className="muted small">{fmtDate(r.at)}</span>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function MoveBin({ bin, onMoved }: { bin: BinDetail; onMoved: () => void }) {
  const locations = useLocations();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const move = async (locationId: string) => {
    setBusy(true);
    setError(null);
    setMsg(null);
    try {
      const r = await api<{ unchanged: boolean; to: string | null; lotCount: number }>('/bins/move', { body: { binId: bin.id, locationId } });
      setMsg(r.unchanged ? 'The bin is already there.' : `${bin.code} and its ${r.lotCount} lot(s) moved to ${r.to}.`);
      onMoved();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };
  const scan = async (code: string) => {
    const { status, body } = await apiRaw(`/locations/by-code/${enc(code)}`).catch(() => ({ status: 0, body: null }));
    if (status === 200) move(body.location.id);
    else setError(new Error(status === 404 ? `No location with code "${code}"` : 'Could not reach the server'));
  };
  return (
    <section className="card">
      <h2>Move the whole bin</h2>
      <ScanField kind="location" label="MOVE BIN TO LOCATION" onScan={scan} busy={busy} />
      <select disabled={busy} value="" onChange={(e) => e.target.value && move(e.target.value)} aria-label="Move bin to location">
        <option value="">…or choose a location</option>
        {locations.data?.map((l) => (
          <option key={l.id} value={l.id}>
            {l.code}
          </option>
        ))}
      </select>
      {msg && <Success>{msg}</Success>}
      <ErrorBox error={error} />
    </section>
  );
}
